import type { FormEvent, ReactNode } from "react";
import { CircleDollarSign, CreditCard, ReceiptText, ShieldCheck, Store } from "lucide-react";
import type { OrganizationDetail } from "../../account";

type OrganizationFinancePageProps = {
  canAdmin: boolean;
  data: OrganizationDetail;
  financialReportsAvailable: boolean;
  isBlocked: (id: string) => boolean;
  locked: (id: string) => ReactNode;
  packageLocked: (title: string, description: string) => ReactNode;
  payment: (event: FormEvent<HTMLFormElement>) => void;
  paymentBusy: boolean;
  permitSalesAvailable: boolean;
  refundOrder: (orderId: string, orderNumber: string) => void | Promise<void>;
};

export function OrganizationFinancePage({
  canAdmin,
  data,
  financialReportsAvailable,
  isBlocked,
  locked,
  packageLocked,
  payment,
  paymentBusy,
  permitSalesAvailable,
  refundOrder,
}: OrganizationFinancePageProps) {
  return (
            <div className="workspace-content">
              <section className="account-stats workspace-stats">
                <article>
                  <CreditCard />
                  <strong>
                    {
                      data.paymentAccounts.filter(
                        (item) => item.onboarding_status === "enabled",
                      ).length
                    }
                    /{data.paymentAccounts.length}
                  </strong>
                  <span>Maksutiliä valmiina</span>
                </article>
                <article>
                  <Store />
                  <strong>{data.offers.length}</strong>
                  <span>Tarjousta</span>
                </article>
                <article>
                  <CircleDollarSign />
                  <strong>{String(data.orderSummary.order_count ?? 0)}</strong>
                  <span>Tilausta</span>
                </article>
                <article>
                  <CircleDollarSign />
                  <strong>
                    {financialReportsAvailable
                      ? (
                          Number(data.orderSummary.paid_total ?? 0) / 100
                        ).toLocaleString("fi-FI", {
                          style: "currency",
                          currency: "EUR",
                        })
                      : "Ei paketissa"}
                  </strong>
                  <span>Maksettu yhteensä</span>
                </article>
              </section>
              <section className="account-panel workspace-section">
                <span className="eyebrow">Maksut</span>
                <h2>Maksupalvelutili</h2>
                <p className="panel-copy">
                  Maksuvalmius kuuluu aina juuri tälle organisaatiolle.
                  Rekisteröinti ei avaa pankki- tai Stripe-tiliä
                  automaattisesti, vaan organisaation ylläpitäjä käynnistää sen
                  erikseen ja Stripe hoitaa tunnistamisen. Lupava.fi ei tallenna
                  kortti- tai pankkitietoja Coreen.
                </p>
                {data.billing.sandbox && data.billing.connectTestPrefill && (
                  <div className="workspace-locked connect-sandbox-note">
                    <ShieldCheck />
                    <div>
                      <b>Stripe-sandbox · testitiedot esitäytetään</b>
                      <p>
                        Yritys, osoite, yhteyshenkilö ja onnistuvan varmennuksen
                        testiarvot täytetään automaattisesti. Stripe pyytää
                        vielä vahvistamaan tiedot, palvelusopimuksen ja
                        mahdollisen testipankkitilin. Testitilan SMS-koodi on
                        000-000. Oikeita henkilö- tai pankkitietoja ei käytetä.
                      </p>
                    </div>
                  </div>
                )}
                {data.paymentAccounts.length > 0 && (
                  <div className="organization-list">
                    {data.paymentAccounts.map((item) => (
                      <article key={String(item.id)}>
                        <CreditCard />
                        <div>
                          <b>{String(item.provider).toUpperCase()}</b>
                          <span>
                            {item.onboarding_status === "enabled"
                              ? "Veloitukset ja tilitykset käytössä"
                              : "Käyttöönotto kesken"}
                          </span>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
                {!permitSalesAvailable
                  ? packageLocked(
                      "Lupien myynti ei kuulu pakettiin",
                      "Maksutili ja myyntitoiminnot avautuvat lupamyyntiä tukevaan pakettiin.",
                    )
                  : isBlocked("payments")
                  ? locked("payments")
                  : canAdmin && (
                      <form
                        className="inline-account-form workspace-form"
                        onSubmit={payment}
                      >
                        <label>
                          Maksupalvelu
                          <select name="provider" defaultValue="stripe">
                            <option value="stripe">Stripe Connect</option>
                          </select>
                        </label>
                        <p className="form-hint">
                          Tilin tunteminen ja pankkitilin vahvistus tehdään
                          Stripen suojatussa palvelussa.
                        </p>
                        <div>
                          <button type="submit" disabled={paymentBusy}>
                            {paymentBusy
                              ? "Avataan Stripeä…"
                              : data.paymentAccounts.length > 0
                                ? "Jatka Stripe-varmennukseen"
                                : "Valmistele maksutili"}
                          </button>
                        </div>
                      </form>
                    )}
              </section>
              <section className="account-panel workspace-section">
                <span className="eyebrow">Tilaukset ja palautukset</span>
                <h2>Viimeisimmät lupatilaukset</h2>
                <p className="panel-copy">
                  Maksun tila tulee Stripe-webhookista. Täysi palautus peruu
                  lupaoikeuden vasta, kun Stripe on vahvistanut palautuksen.
                </p>
                {data.orders.length > 0 ? (
                  <div className="account-item-list">
                    {data.orders.map((order) => (
                      <article key={order.id}>
                        <ReceiptText />
                        <div>
                          <b>{order.order_number}</b>
                          <span>
                            {order.customer_name} ·{" "}
                            {(order.total_amount / 100).toLocaleString("fi-FI", {
                              style: "currency",
                              currency: order.currency,
                            })}{" "}
                            · {order.status}
                          </span>
                        </div>
                        {canAdmin &&
                          ["paid", "partially_refunded"].includes(
                            order.status,
                          ) &&
                          data.capabilities.some(
                            (item) =>
                              item.capability === "handle_refunds" &&
                              item.status === "verified",
                          ) && (
                            <button
                              className="secondary"
                              onClick={() =>
                                void refundOrder(order.id, order.order_number)
                              }
                            >
                              Palauta maksu
                            </button>
                          )}
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="workspace-readonly">
                    Maksuja tai tilauksia ei ole vielä.
                  </div>
                )}
              </section>
            </div>
  );
}

